import type { ChallengeType } from '../content';

/** The minimum a rule needs to know about a track (PRODUCT.md §5.1). */
export type StructureChallenge = { id: string; type: ChallengeType; isGuest: boolean };
export type StructureChapter = { id: string; challenges: readonly StructureChallenge[] };
export type TrackStructure = { chapters: readonly StructureChapter[] };

export type ChallengeUnlockState = 'locked' | 'available';
export type ChapterUnlockState = 'locked' | 'available' | 'completed';

export type Unlocks = {
  challenges: Map<string, ChallengeUnlockState>;
  chapters: Map<string, ChapterUnlockState>;
};

export type UnlockOptions = { guest: boolean };

export type UnlockedItem = { kind: 'challenge' | 'chapter'; id: string };

function toSet(ids: Iterable<string>): ReadonlySet<string> {
  return ids instanceof Set ? ids : new Set(ids);
}

function bossOf(chapter: StructureChapter): StructureChallenge | undefined {
  return chapter.challenges.find((c) => c.type === 'boss');
}

/** A chapter's gate is passed when its boss is completed (or, without a boss, every challenge is). */
function gatePassed(chapter: StructureChapter, done: ReadonlySet<string>): boolean {
  const boss = bossOf(chapter);
  return boss ? done.has(boss.id) : chapter.challenges.every((c) => done.has(c.id));
}

/**
 * - Chapter 1 is unlocked for everyone; chapter N+1 unlocks when chapter N's boss is completed.
 * - In an unlocked chapter every non-boss challenge is available; the boss needs all the others completed.
 * - Guests can only play `isGuest` challenges.
 */
export function computeUnlocks(
  structure: TrackStructure,
  completedIds: Iterable<string>,
  { guest }: UnlockOptions,
): Unlocks {
  const done = toSet(completedIds);
  const challenges = new Map<string, ChallengeUnlockState>();
  const chapters = new Map<string, ChapterUnlockState>();

  let previousGatePassed = true;
  structure.chapters.forEach((chapter, index) => {
    const chapterOpen = guest ? index === 0 : previousGatePassed;
    const allDone = chapter.challenges.length > 0 && chapter.challenges.every((c) => done.has(c.id));
    chapters.set(chapter.id, !chapterOpen ? 'locked' : allDone ? 'completed' : 'available');

    const othersDone = chapter.challenges.filter((c) => c.type !== 'boss').every((c) => done.has(c.id));
    for (const challenge of chapter.challenges) {
      let open = chapterOpen && (challenge.type !== 'boss' || othersDone);
      if (guest) open = open && challenge.isGuest;
      challenges.set(challenge.id, open ? 'available' : 'locked');
    }
    previousGatePassed = chapterOpen && gatePassed(chapter, done);
  });

  return { challenges, chapters };
}

/**
 * For a signed-in learner: the id of the challenge to finish before `challengeId` opens, or `null` when it's
 * not locked (or unknown). Points at the earliest unfinished requirement so the "Finish X first" link is actionable.
 */
export function lockedBy(
  structure: TrackStructure,
  completedIds: Iterable<string>,
  challengeId: string,
): string | null {
  const done = toSet(completedIds);
  const unlocks = computeUnlocks(structure, done, { guest: false });
  if (unlocks.challenges.get(challengeId) !== 'locked') return null;

  // The first chapter that either holds the target or hasn't passed its gate is always open (every earlier gate
  // passed), so its first unfinished non-boss challenge — or else its boss — is playable right now.
  const blocking = structure.chapters.find(
    (chapter) => chapter.challenges.some((c) => c.id === challengeId) || !gatePassed(chapter, done),
  )!;
  const next = blocking.challenges.find((c) => c.type !== 'boss' && !done.has(c.id)) ?? bossOf(blocking)!;
  return next.id;
}

/** What became available between two unlock snapshots (for the attempt response). */
export function diffUnlocks(before: Unlocks, after: Unlocks): UnlockedItem[] {
  const unlocked: UnlockedItem[] = [];
  for (const [id, state] of after.chapters) {
    if (before.chapters.get(id) === 'locked' && state !== 'locked') unlocked.push({ kind: 'chapter', id });
  }
  for (const [id, state] of after.challenges) {
    if (before.challenges.get(id) === 'locked' && state === 'available')
      unlocked.push({ kind: 'challenge', id });
  }
  return unlocked;
}
